import { NgClass } from '@angular/common';
import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { OutlookNotificationStatusType } from '../../constants/notification';

@Component({
  selector: 'maximizer-outlook-status-message',
  template: ` <div
    class="p-2 border flex flex-col gap-2"
    [ngClass]="containerClass()"
  >
    <div class="flex items-center gap-2">
      @if (icon()) {
        <span
          class="fa-icons icon-20 icon-solid mt-[2px]"
          [class]="[iconClass(), 'icon-' + icon()]"
        ></span>
      }

      @if (message()) {
        <p
          class="text-body"
          [class]="textClass()"
          [innerHTML]="message()"
          (click)="onMessageClick($event)"
        ></p>
      }
    </div>

    @if (actionText() || dismissText()) {
      <div class="flex items-center justify-end gap-2">
        @if (dismissText()) {
          <button
            kendoButton
            themeColor="tertiary"
            class="text-body bold"
            (click)="dismiss.emit()"
          >
            {{ dismissText() }}
          </button>
        }

        @if (actionText()) {
          <button
            [id]="actionButtonId()"
            kendoButton
            themeColor="secondary"
            class="text-body bold"
            (click)="action.emit()"
          >
            {{ actionText() }}
          </button>
        }
      </div>
    }
  </div>`,
  styles: `
    :host {
      --kendo-color-secondary: transparent;
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, KENDO_BUTTON],
})
export class StatusMessageComponent {
  type = input<OutlookNotificationStatusType>('warning');
  icon = input('circle-exclamation');
  message = input<string>();
  actionText = input<string>();
  dismissText = input<string>();
  actionButtonId = input<string | null>(null);
  disableDefaultLinkNavigation = input(false);

  action = output<void>();
  dismiss = output<void>();
  messageLinkClick = output<string>();

  onMessageClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest('a');
    const href = anchor?.getAttribute('href');

    if (!href) {
      return;
    }

    if (this.disableDefaultLinkNavigation()) {
      event.preventDefault();
    }

    this.messageLinkClick.emit(href);
  }

  protected readonly containerClass = computed(() => {
    switch (this.type()) {
      case 'error':
        return 'bg-vertical3-5 border-vertical3-90';
      case 'info':
        return 'bg-inserv-0 border-inserv-110';
      default:
        return 'bg-authorized-5 border-authorized-100';
    }
  });

  protected readonly textClass = computed(() => {
    switch (this.type()) {
      case 'error':
        return 'text-error';
      case 'info':
        return 'text-inserv-110';
      default:
        return 'text-authorized-100';
    }
  });

  protected readonly iconClass = computed(() => {
    switch (this.type()) {
      case 'error':
        return 'text-error';
      case 'info':
        return 'text-inserv-110';
      default:
        return 'text-authorized-100';
    }
  });
}
